import React, { useState } from 'react'
import { Box, Flex, Spinner, Text, Button, Icon } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { fetchDocument, fetchTemplate } from '../../api/document/document'
import { DownloadIcon } from '@chakra-ui/icons'

interface DocumentViewerProps {
  documentId: string
  readOnly?: boolean
  useDocumentQuery?: boolean // NEW: if true, use fetchDocument instead of fetchTemplate
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentId, readOnly = false, useDocumentQuery = false }) => {
  const [useSigned, setUseSigned] = useState(true)

  const { data: document, isLoading, error } = useQuery({
    queryKey: ['documentViewer', documentId, useDocumentQuery],
    queryFn: () => useDocumentQuery ? fetchDocument(documentId) : fetchTemplate(documentId),
    enabled: !!documentId,
  })

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="300px">
        <Spinner size="lg" color="primary.100" />
      </Flex>
    )
  }

  if (error || !document) {
    return (
      <Box textAlign="center" p={8}>
        <Text color="solid.500">Could not load document. Please try again later.</Text>
      </Box>
    )
  }

  // Determine file URL (use signed version if available and requested)
  const fileUrl = useSigned && document.signed_file ? document.signed_file : document.file
  
  // Determine file type from URL if not explicitly provided
  const getFileType = () => {
    if (document.file_type) return document.file_type.toLowerCase()
    
    const url = fileUrl.toLowerCase()
    const extensions = [
      'pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 
      'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'
    ]
    
    for (const ext of extensions) {
      if (url.includes(`.${ext}`)) return ext
    }
    
    return 'unknown'
  }
  
  const fileType = getFileType()


  // Render appropriate document view based on type
  const renderDocumentContent = () => {
    if (fileType === 'pdf') {
      return (
        <Box height="600px" width="100%">
          <iframe 
            src={`${fileUrl}#toolbar=${readOnly ? '0' : '1'}`} 
            width="100%" 
            height="100%" 
            style={{ border: 'none' }}
            title={document.title}
          />
        </Box>
      )
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) {
      return (
        <Box textAlign="center" maxH="600px" overflow="auto">
          <img 
            src={fileUrl} 
            alt={document.title} 
            style={{ 
              maxWidth: '100%', 
              margin: '0 auto',
              maxHeight: readOnly ? '550px' : 'none' 
            }} 
          />
        </Box>
      )
    } else {
      // Fallback for other document types
      return (
        <Box textAlign="center" p={8}>
          <Text mb={4}>This document type ({fileType}) cannot be previewed directly.</Text>
          <Button
            leftIcon={<Icon as={DownloadIcon} />}
            variant="mono"
            onClick={() => {
              if (fileUrl) {
                window.open(fileUrl, '_blank')
              }
            }}
          >
            {fileType === 'unknown' ? 'Open Document' : 'Download Document'}
          </Button>
        </Box>
      )
    }
  }

  return (
    <Box width="100%">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontWeight="bold" fontSize="lg" isTruncated>{document.title}</Text>
        <Flex align="center">
          
          
          {document.signed_file && (
            <Button
              size="sm"
              variant={useSigned ? "solid" : "outline"}
              colorScheme="primary"
              onClick={() => setUseSigned(!useSigned)}
              mr={2}
            >
              {useSigned ? "Signed Version" : "Original Version"}
            </Button>
          )}
          
          <Button
            size="sm"
            leftIcon={<Icon as={DownloadIcon} />}
            variant="outline"
            onClick={() => {
              if (fileUrl) {
                window.open(fileUrl, '_blank')
              }
            }}
          >
            Download
          </Button>
        </Flex>
      </Flex>
      
      {renderDocumentContent()}
      
      <Flex justify="flex-end" mt={4}>
        <Text fontSize="sm" color="solid.500">
          Last Updated: {new Date(document.updated_at).toLocaleDateString()}
        </Text>
      </Flex>
    </Box>
  )
}

export default DocumentViewer